import {
    api,
    apiErrorMessage,
    formatMoney,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    AssistantButton,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateTime,
    DetailRow,
    FieldGrid,
    formatBytes,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    SearchableSelect,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Dispute } from '@/api/types';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const OUTCOMES = ['won', 'lost', 'accepted', 'expired'] as const;

/** The reads of `/disputes/:id` (BM-46 §2.0). */
export const disputeReads = (id: string | undefined) => ({
    dispute: oneSpec(`/staff/disputes/${id ?? ''}`),
});

export function DisputePage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const query = useOne<Dispute>(disputeReads(id).dispute.path);
    const [closing, setClosing] = useState(false);
    const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]>('won');
    const [error, setError] = useState<string | null>(null);
    const close = useWrite(
        async (o: string) =>
            (
                await api().post<Dispute>(`/staff/disputes/${id}/close`, {
                    outcome: o,
                })
            ).data,
        ['/staff/disputes', '/staff/queues', '/staff/cases'],
    );
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Disputes', href: '/disputes' }]}
                // design 1.5.1 (bm-127-design-v1.5.1 § 2): a chargeback is named by its amount (USD in v1)
                {...detailTitle(
                    query,
                    query.data
                        ? `Chargeback of ${formatMoney(query.data.data.amount_cents, 'USD')}`
                        : null,
                    id,
                    'Dispute',
                )}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    <AssistantButton
                        surface="dispute"
                        subject={{ type: 'dispute', id }}
                    />
                }
            />
            <DetailBody query={query} title="Dispute">
                {(d) => (
                    <div className="space-y-4">
                        <Card>
                            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                <CardTitle className="text-base">
                                    <StatusBadge
                                        status={d.status}
                                        domain="dispute"
                                    />
                                    {d.outcome ? (
                                        <span className="ml-2 text-sm">
                                            {d.outcome}
                                        </span>
                                    ) : null}
                                    {d.over_disputed ? (
                                        <span className="ml-2">
                                            <StatusBadge
                                                status="over_disputed"
                                                domain={
                                                    ADMIN_STATUSES.disputeFlag
                                                }
                                            />
                                        </span>
                                    ) : null}
                                </CardTitle>
                                {can(P.disputesRespond) &&
                                (d.status === 'open' ||
                                    d.status === 'evidence_submitted') ? (
                                    <Button
                                        size="sm"
                                        onClick={() => setClosing(true)}
                                    >
                                        Close with outcome
                                    </Button>
                                ) : null}
                            </CardHeader>
                            <CardContent>
                                <FieldGrid>
                                    <DetailRow
                                        label="Merchant"
                                        layout="stacked"
                                    >
                                        <ObjectRef id={d.merchant_id} />
                                    </DetailRow>
                                    <DetailRow
                                        label="Reason code"
                                        value={d.reason_code ?? '—'}
                                        layout="stacked"
                                    />
                                    <DetailRow label="Case id" layout="stacked">
                                        <span className="font-mono text-xs">
                                            {d.provider_case_id ?? '—'}
                                        </span>
                                    </DetailRow>
                                    <DetailRow label="Amount" layout="stacked">
                                        <Money cents={d.amount_cents} /> plus a{' '}
                                        <Money cents={d.fee_cents} /> fee
                                    </DetailRow>
                                    <DetailRow
                                        label="Charge"
                                        value={d.charge_id}
                                        layout="stacked"
                                    />
                                    <DetailRow
                                        label="Order"
                                        value={d.order_id ?? '—'}
                                        layout="stacked"
                                    />
                                    <DetailRow label="Opened" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={d.opened_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow
                                        label="Merchant evidence due"
                                        layout="stacked"
                                    >
                                        {/* BM-127 decision 5: 3 business days in the tenant's business zone */}
                                        <DateTime
                                            zone="inline"
                                            value={d.merchant_evidence_due_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow
                                        label="Network deadline"
                                        layout="stacked"
                                    >
                                        <DateTime
                                            zone="inline"
                                            value={d.evidence_due_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow label="Closed" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={d.closed_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                </FieldGrid>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-medium">
                                    Evidence from the merchant
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="divide-y text-sm">
                                    {d.evidence.map((e, i) => (
                                        <li
                                            key={e.file_id ?? i}
                                            className="flex flex-col gap-1 py-2 md:flex-row md:items-center md:justify-between md:gap-4"
                                            data-testid="evidence-item"
                                        >
                                            <span className="min-w-0">
                                                <span className="font-medium">
                                                    {e.type}
                                                </span>
                                                {e.note ? (
                                                    <span className="text-muted-foreground">
                                                        {' '}
                                                        — {e.note}
                                                    </span>
                                                ) : null}
                                            </span>
                                            {e.file_id ? (
                                                // BM-43: private files only leave Bemor as an attachment through the staff route.
                                                <a
                                                    href={`/api/staff/disputes/${d.id}/evidence/${e.file_id}`}
                                                    download
                                                    className="min-h-11 shrink-0 text-sm md:min-h-0"
                                                >
                                                    {e.file_name ?? 'Download'}
                                                    {e.size_bytes
                                                        ? ` (${formatBytes(e.size_bytes)})`
                                                        : ''}
                                                </a>
                                            ) : (
                                                <span className="text-muted-foreground text-xs">
                                                    Stored the old way, no
                                                    download
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                    {d.evidence.length === 0 ? (
                                        <li className="text-muted-foreground py-2">
                                            No evidence yet.
                                        </li>
                                    ) : null}
                                </ul>
                            </CardContent>
                        </Card>
                        <ConfirmDialog
                            open={closing}
                            onOpenChange={setClosing}
                            title="Close this dispute"
                            description="Won returns the amount and the fee to the merchant; lost and accepted keep the chargeback postings."
                            confirmLabel="Close dispute"
                            busy={close.isPending}
                            onConfirm={() => {
                                setError(null);
                                close.mutate(outcome, {
                                    onSuccess: () => setClosing(false),
                                    onError: (e) =>
                                        setError(apiErrorMessage(e)),
                                });
                            }}
                        >
                            <SearchableSelect
                                label="Outcome"
                                options={optionsOf(OUTCOMES)}
                                value={outcome}
                                clearable={false}
                                onChange={(v) =>
                                    v
                                        ? setOutcome(v as typeof outcome)
                                        : undefined
                                }
                            />
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-xs"
                                >
                                    {error}
                                </p>
                            ) : null}
                        </ConfirmDialog>
                    </div>
                )}
            </DetailBody>
            <div className="mt-4">
                <Comments subjectType="dispute" subjectId={id} />
            </div>
        </div>
    );
}
